import React from 'react';
import {useDispatch} from 'react-redux';

import Login from 'web-app/react/login/login';
import * as LoginActions from 'web-app/react/login/actions';
import {useQueryParams} from 'web-app/react/hooks/use-query-params';
import {setLocale} from 'web-app/util/locale';
import {exhaustiveCheck, hasValue} from 'web-app/util/typescript';
import LocalStorage from 'web-app/services/local-storage';
import {ACCESS_TOKEN_KEY} from 'web-app/util/access-token-helpers';
import {navigationContext} from 'web-app/react/contexts/navigation';
import {SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM} from 'web-app/react/routes/constants';
import Spinner from 'web-app/react/components/loading/spinner/spinner';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';

import {ExistingUserSelfSignupQueryParam} from './constants';

type LocaleState = 'Undetermined' | 'ShouldNotFetch' | 'IsFetching' | 'IsDoneFetching';
type LoggedInState = 'Undetermined' | 'IsLoggedIn' | 'IsNotLoggedIn';

const LoginRoute: React.FC<React.PropsWithChildren<{}>> = () => {
    const {email, password, returnUrl, locale, token, existingUserSelfSignup} = useQueryParams([
        'email',
        'password',
        'returnUrl',
        'locale',
        'token',
        ExistingUserSelfSignupQueryParam,
    ]);

    const isExistingUserSelfSignup = hasValue(existingUserSelfSignup) && existingUserSelfSignup !== '';

    const [localeState, setLocaleState] = React.useState<LocaleState>('Undetermined');
    const [loggedInState, setLoggedInState] = React.useState<LoggedInState>('Undetermined');
    const loadingTokenAuthentication = useTypedSelector(state => state.login.loadingTokenAuthentication);

    const {replaceWith, generateUrl} = React.useContext(navigationContext);
    const dispatch = useDispatch();

    // The 'loggedInState' prevents "flashing" the login page when we want to redirect away from it anyway.
    React.useEffect(() => {
        if (LocalStorage.getFromLocalStorage(ACCESS_TOKEN_KEY)) {
            // If we have an accessToken from localStorage we can just go straight to the app
            setLoggedInState('IsLoggedIn');

            if (returnUrl) {
                window.location.replace(decodeURIComponent(returnUrl));
            } else {
                replaceWith('account', {
                    queryParams: {[SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM]: 'true'},
                });
            }
        } else if (email && password) {
            // Otherwise attempt to login if email and password is passed via query params
            setLoggedInState('IsLoggedIn');
            dispatch(
                LoginActions.credentialLoginForRedirect.action(
                    {email, password},
                    {
                        redirectUrl: generateUrl('account', {
                            queryParams: {[SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM]: 'true'},
                        }),
                    },
                ),
            );
        } else if (token) {
            // Otherwise attempt to login if a JWT is passed via query params
            setLoggedInState('IsLoggedIn');
            dispatch(LoginActions.tokenLogin.action({token}, {redirectUrl: returnUrl}));
        } else {
            setLoggedInState('IsNotLoggedIn');
        }
    }, [dispatch, generateUrl, replaceWith, email, password, returnUrl, token]);

    // We shouldn't mount the UI before we've downloaded necessary locale files
    React.useEffect(() => {
        if (locale) {
            setLocaleState('IsFetching');
            setLocale(locale).finally(() => setLocaleState('IsDoneFetching'));
        } else {
            setLocaleState('ShouldNotFetch');
        }

        return () => setLocaleState('Undetermined');
    }, [locale]);

    if (loadingTokenAuthentication) {
        return <Spinner margined centered />;
    }

    switch (localeState) {
        case 'Undetermined':
        case 'IsFetching':
            return null;
        case 'IsDoneFetching':
        case 'ShouldNotFetch':
            switch (loggedInState) {
                case 'Undetermined':
                case 'IsLoggedIn':
                    return null;
                case 'IsNotLoggedIn':
                    return (
                        <Login
                            email={email || undefined}
                            redirectUrl={returnUrl || undefined}
                            locale={locale || undefined}
                            isExistingUserSelfSignup={isExistingUserSelfSignup}
                        />
                    );
                default:
                    exhaustiveCheck(loggedInState);
                    return null;
            }
        default:
            exhaustiveCheck(localeState);
            return null;
    }
};

export default LoginRoute;
